import React from 'react';
import { formatDateTime } from '../utils/dateUtils';
import { Wifi, RefreshCw } from 'lucide-react';

function simplifyLiveError(error = '') {
  const text = String(error || '');
  if (!text) return '';
  const lower = text.toLowerCase();
  if (lower.includes('some live feeds are delayed') || lower.includes('verified backup data') || lower.includes('recovered') || lower.includes('fallback')) {
    return 'Core systems operational — using verified backup data where needed.';
  }
  if (lower.includes('partial/degraded') || lower.includes('configured safety') || lower.includes('last-clean') || lower.includes('usable rough signal')) {
    return 'Live feeds delayed — using verified backup data while feeds reconnect.';
  }
  if (lower.includes('not enough live api data') || lower.includes('safe placeholder')) {
    return 'Core feeds missing — showing safe placeholder guidance until data reconnects.';
  }
  return text;
}

export default function LiveModeIndicator({ mode, lastUpdated, isLoading, error, onRefresh, onOpenLearn, actionLabel = 'Learn Scenarios' }) {
  if (mode === 'CHECKING') {
    return (
      <div className="glass-card p-4 animate-pulse">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-mentor-accent animate-pulse" />
          <span className="text-xs font-display font-semibold text-mentor-muted uppercase tracking-widest">
            Detecting data source...
          </span>
        </div>
      </div>
    );
  }

  if (mode === 'LIVE') {
    return (
      <div className="glass-card p-4 border border-signal-green/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-signal-green animate-pulse" style={{ boxShadow: '0 0 8px rgba(34,197,94,0.5)' }} />
              <Wifi size={14} className="text-signal-green" />
              <span className="text-xs font-display font-bold text-signal-green uppercase tracking-widest">
                {error ? 'VERIFIED BACKUP MODE' : 'LIVE DATA'}
              </span>
            </div>
            {lastUpdated && (
              <span className="text-xs text-mentor-muted font-mono">
                Updated: {formatDateTime(lastUpdated)}
              </span>
            )}
            {error && (
              <span className="text-xs text-signal-yellow font-mono">
                ⚠ {simplifyLiveError(error)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-display font-medium border border-mentor-border/40 text-mentor-muted hover:text-mentor-text hover:border-mentor-border transition-all"
            >
              <RefreshCw size={12} className={isLoading ? 'animate-spin' : ''} />
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              type="button"
              onClick={onOpenLearn}
              className="px-3 py-1.5 rounded-lg text-xs font-display font-semibold border border-mentor-accent/40 text-mentor-accent hover:bg-mentor-accent/10 transition-all"
            >
              {actionLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // DEMO mode
  return null; // The ScenarioSwitcher handles demo mode display
}
